import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save } from "lucide-react";
import InspectionQrHandoff from "../components/InspectionQrHandoff";
import ParkingPhotoCapture from "../components/ParkingPhotoCapture";
import { formatBookingError } from "../lib/bookingErrors";
import {
  PARKING_FACILITIES,
  PARKING_SPACE_TYPES,
  EDITABLE_PARKING_STATUSES,
  buildParkingPayload,
  validateParkingHours,
} from "../lib/parkingListingConstants";
import { mergeParkingPhotoSync, parkingPhotosComplete, parkingPhotosToUrls, urlsToParkingPhotos } from "../lib/parkingPhotos";

export default function EditParking() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [parkingPhotos, setParkingPhotos] = useState({});
  const [qrToken, setQrToken] = useState(null);

  useEffect(() => {
    // Don't fetch until AuthContext has resolved — avoids anonymous-user 404s
    // on pending/rejected listings that aren't visible to the public.
    if (authLoading) return;

    (async () => {
      try {
        let s;
        if (user?.is_admin) {
          // Admin endpoint bypasses visibility filter and works for any status.
          const r = await axios.get(`${API}/admin/parking/${id}/review`, { withCredentials: true });
          s = r.data.spot;
        } else {
          const r = await axios.get(`${API}/parking/${id}`, { withCredentials: true });
          s = r.data;
        }
        if (!user?.is_admin && s.host_id !== user?.user_id) {
          toast.error("You don't have permission to edit this listing");
          navigate("/host?tab=parking");
          return;
        }
        if (!EDITABLE_PARKING_STATUSES.has(s.status)) {
          toast.error("This listing cannot be edited in its current state");
          navigate("/host?tab=parking");
          return;
        }
        setStatus(s.status);
        setRejectionReason(s.rejection_reason || "");
        setForm({
          name: s.name || "",
          address: s.address || "",
          city: s.city || "",
          space_type: s.space_type || "driveway",
          price_per_hour: s.price_per_hour ?? 2,
          max_hours: s.max_hours ?? "",
          open_24_7: !!s.open_24_7,
          open_time: s.open_time || "08:00",
          close_time: s.close_time || "22:00",
          facilities: s.facilities || [],
          description: s.description || "",
          images: s.images || [],
        });
        setParkingPhotos(urlsToParkingPhotos(s.images || []));
      } catch (e) {
        const httpStatus = e.response?.status;
        if (httpStatus === 404) {
          toast.error("Listing not found");
        } else if (httpStatus === 403) {
          toast.error("You don't have permission to edit this listing");
        } else {
          toast.error(e.response?.data?.detail || "Could not load listing");
        }
        navigate("/host?tab=parking");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user, authLoading, navigate]);

  const toggleFac = (val) => setForm((f) => ({
    ...f,
    facilities: f.facilities.includes(val) ? f.facilities.filter((x) => x !== val) : [...f.facilities, val],
  }));

  const submit = async (e, resubmit = false) => {
    e.preventDefault();
    if (!form) return;
    if (!(form.address || "").trim() || !(form.city || "").trim()) {
      toast.error("Enter address and city");
      return;
    }
    if ((form.images || []).length === 0 && !parkingPhotosComplete(parkingPhotos)) {
      toast.error("Add at least one listing photo — use your camera, upload, or scan the QR on your phone");
      return;
    }
    const hoursErr = validateParkingHours(form);
    if (hoursErr) {
      toast.error(hoursErr);
      return;
    }
    setSubmitting(true);
    try {
      const images = parkingPhotosComplete(parkingPhotos) ? parkingPhotosToUrls(parkingPhotos) : form.images;
      const payload = buildParkingPayload(form, images);
      const r = await axios.patch(`${API}/parking/${id}`, payload, { withCredentials: true });
      if (resubmit || status === "rejected") {
        await axios.post(`${API}/parking/${id}/resubmit`, {}, { withCredentials: true });
        toast.success("Listing updated and resubmitted for review");
      } else if (r.data?.status === "pending" && (status === "approved" || status === "paused")) {
        toast.success("Changes saved — listing is back in the admin review queue");
      } else {
        toast.success("Listing updated");
      }
      navigate("/host?tab=parking");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "listing_not_rejected") toast.error("Only rejected listings can be resubmitted");
      else toast.error(formatBookingError(d) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !form) {
    return <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12 text-[var(--zc-text-2)]" data-testid="edit-parking-loading">Loading…</div>;
  }

  const needsReReview = status === "approved" || status === "paused";

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12" data-testid="edit-parking-page">
      <div className="eyebrow mb-2">Host</div>
      <h1 className="zc-headline text-4xl md:text-5xl">Edit parking listing</h1>
      <p className="text-[var(--zc-text-2)] mt-3">
        {needsReReview
          ? "Saving changes to a live listing sends it back to admin for re-approval before it appears in search again."
          : "Update your parking spot details."}
      </p>

      {status === "rejected" && (
        <div className="mt-6 p-4 rounded-lg border border-[var(--zc-border)]" style={{ background: "rgba(220,38,38,0.06)" }} data-testid="ep-rejected-banner">
          <div className="font-semibold text-[var(--zc-text)]">Listing rejected</div>
          <p className="text-sm text-[var(--zc-text-2)] mt-1">
            {rejectionReason || "Please review your listing details and resubmit for admin approval."}
          </p>
        </div>
      )}

      <form onSubmit={(e) => submit(e, false)} className="space-y-6 mt-10" data-testid="edit-parking-form">
        <div className="zc-card p-5 md:p-6">
          <div className="eyebrow mb-4">Parking information</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name or lot #" testid="ep-name" v={form.name} on={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="Downtown Driveway" />
            <Field label="Address" testid="ep-address" v={form.address} on={(v) => setForm((f) => ({ ...f, address: v }))} required placeholder="150 Biscayne Crescent" />
            <Field label="City" testid="ep-city" v={form.city} on={(v) => setForm((f) => ({ ...f, city: v }))} required placeholder="Brampton" />
            <Field label="Price per hour ($)" type="number" testid="ep-rate" v={form.price_per_hour} on={(v) => setForm((f) => ({ ...f, price_per_hour: v }))} />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <Field label="Max stay per booking (hours)" type="number" testid="ep-max-hours" v={form.max_hours} on={(v) => setForm((f) => ({ ...f, max_hours: v }))} placeholder="e.g. 3" hint="Leave blank if renters can stay the full operating window." />
          </div>
        </div>

        <div className="zc-card p-5 md:p-6" data-testid="ep-space-type-card">
          <div className="eyebrow mb-2">Space type</div>
          <p className="text-xs text-[var(--zc-text-3)] mb-4">Choose one space type for this listing.</p>
          <div className="flex flex-wrap gap-2" data-testid="ep-type">
            {PARKING_SPACE_TYPES.map(([val, lbl]) => (
              <button key={val} type="button" onClick={() => setForm((f) => ({ ...f, space_type: val }))}
                className={"px-3 h-9 text-xs zc-chip " + (form.space_type === val ? "zc-chip-active" : "")}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="zc-card p-5 md:p-6" data-testid="ep-amenities-card">
          <div className="eyebrow mb-2">Amenities</div>
          <p className="text-xs text-[var(--zc-text-3)] mb-4">Select all that apply.</p>
          <div className="flex flex-wrap gap-2">
            {PARKING_FACILITIES.map(([val, lbl, icon]) => (
              <button key={val} type="button" onClick={() => toggleFac(val)}
                className={"px-3 h-9 inline-flex items-center gap-1.5 text-xs zc-chip " + (form.facilities.includes(val) ? "zc-chip-active" : "")}>
                {icon} {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="zc-card p-5 md:p-6">
          <div className="eyebrow mb-3">Hours</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.open_24_7} onChange={(e) => setForm((f) => ({ ...f, open_24_7: e.target.checked }))} className="w-4 h-4 accent-[var(--zc-accent)]" data-testid="ep-247" />
            Open 24/7
          </label>
          {!form.open_24_7 && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <TimeField label="Open" testid="ep-open" v={form.open_time} on={(v) => setForm((f) => ({ ...f, open_time: v }))} />
              <TimeField label="Close" testid="ep-close" v={form.close_time} on={(v) => setForm((f) => ({ ...f, close_time: v }))} />
            </div>
          )}
          {form.open_24_7 && (!form.max_hours || Number(form.max_hours) >= 24) && (
            <p className="text-caption text-[var(--zc-success)] mt-3">24-hour parking available</p>
          )}
          {form.max_hours && Number(form.max_hours) < 24 && (
            <p className="text-xs text-[var(--zc-text-2)] mt-3">Max stay: {form.max_hours} hours per booking</p>
          )}
        </div>

        <div className="zc-card p-5 md:p-6">
          <div className="eyebrow mb-3">Description</div>
          <textarea className="zc-input w-full p-3 text-sm h-28" value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="ep-desc" />
        </div>

        <div className="zc-card p-5 md:p-6" data-testid="ep-photos-section">
          <div className="eyebrow mb-1">Photos</div>
          <p className="text-xs text-[var(--zc-text-3)] mb-4">
            Show renters the space — capture on this device or scan the QR to use your phone camera.
          </p>
          <InspectionQrHandoff
            phase="parking_listing"
            parkingLabel={[form.name, form.address, form.city].filter(Boolean).join(" · ") || undefined}
            active
            photos={parkingPhotos}
            onPhotosSync={(incoming) => {
              setParkingPhotos((prev) => {
                const merged = mergeParkingPhotoSync(prev, incoming);
                setForm((f) => ({ ...f, images: parkingPhotosToUrls(merged) }));
                return merged;
              });
            }}
            onToken={setQrToken}
            testid="ep-photo-qr"
          />
          <ParkingPhotoCapture
            value={parkingPhotos}
            onChange={(next) => {
              setParkingPhotos(next);
              setForm((f) => ({ ...f, images: parkingPhotosToUrls(next) }));
            }}
            qrToken={qrToken}
            testid="ep-photo-capture"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--zc-border)]">
          <button type="button" onClick={() => navigate("/host?tab=parking")} className="px-4 h-11 zc-btn-ghost text-sm" data-testid="ep-cancel">Cancel</button>
          {status === "rejected" ? (
            <button type="button" disabled={submitting} onClick={(e) => submit(e, true)} className="px-5 h-11 zc-btn-primary text-sm flex items-center gap-2" data-testid="ep-resubmit">
              <Save size={14} /> {submitting ? "Saving…" : "Save & resubmit"}
            </button>
          ) : (
            <button type="submit" disabled={submitting} className="px-5 h-11 zc-btn-primary text-sm flex items-center gap-2" data-testid="ep-submit">
              <Save size={14} /> {submitting ? "Saving…" : "Save changes"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function TimeField({ label, v, on, testid }) {
  return (
    <div>
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <input
        type="time"
        required
        className="zc-input w-full h-11 px-3 text-sm mt-1"
        value={v}
        onChange={(e) => on(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function Field({ label, v, on, type = "text", required, placeholder, testid, hint }) {
  return (
    <div>
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <input type={type} required={required} placeholder={placeholder} className="zc-input w-full h-11 px-3 text-sm mt-1" value={v} onChange={(e) => on(e.target.value)} data-testid={testid} />
      {hint && <p className="text-xs text-[var(--zc-text-3)] mt-1">{hint}</p>}
    </div>
  );
}
